/**
 * useSeriesState - Custom hook for Series module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialSeriesForm = {
  name: '',
  description: '',
  coverImage: '',
  isActive: true,
  order: 0,
  isNew: true
};

export const useSeriesState = ({ setError, setSuccess }) => {
  // State
  const [cmsSeries, setCmsSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [showSeriesModal, setShowSeriesModal] = useState(false);

  // Fetch all series
  const fetchCmsSeries = useCallback(async () => {
    setLoadingSeries(true);
    try {
      const response = await axios.get(`${API_URL}/api/cms/series`);
      setCmsSeries(response.data || []);
    } catch (err) {
      console.error('Error fetching series:', err);
      setError('Failed to load series');
    } finally {
      setLoadingSeries(false);
    }
  }, [setError]);

  // Create new series
  const handleCreateSeries = useCallback(() => {
    setEditingSeries({ ...initialSeriesForm });
    setShowSeriesModal(true);
  }, []);

  // Edit existing series
  const handleEditSeries = useCallback((series) => {
    setEditingSeries({ ...series });
    setShowSeriesModal(true);
  }, []);

  // Save series (create or update)
  const handleSaveSeries = useCallback(async () => {
    if (!editingSeries) return;

    if (!editingSeries.name?.trim()) {
      setError('Series name is required');
      return;
    }

    try {
      if (editingSeries.isNew) {
        const { isNew, ...seriesData } = editingSeries;
        const response = await axios.post(`${API_URL}/api/cms/series`, seriesData, {
          headers: getAuthHeaders()
        });
        setCmsSeries(prev => [...prev, response.data.series]);
        setSuccess(`"${editingSeries.name}" created successfully!`);
      } else {
        await axios.put(`${API_URL}/api/cms/series/${editingSeries.id}`, editingSeries, {
          headers: getAuthHeaders()
        });
        setCmsSeries(prev => prev.map(s => s.id === editingSeries.id ? editingSeries : s));
        setSuccess(`"${editingSeries.name}" saved successfully!`);
      }

      setShowSeriesModal(false);
      setEditingSeries(null);
      // Refresh to get updated episode counts
      fetchCmsSeries();
    } catch (err) {
      setError('Failed to save series: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingSeries, fetchCmsSeries, setError, setSuccess]);

  // Delete series
  const handleDeleteSeries = useCallback(async (seriesId) => {
    const series = cmsSeries.find(s => s.id === seriesId);
    if (!series) return;

    if (series.episodeCount > 0) {
      setError(`Cannot delete "${series.name}" - it has ${series.episodeCount} episode(s) assigned. Please reassign those episodes first.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${series.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/cms/series/${seriesId}`, {
        headers: getAuthHeaders()
      });
      setCmsSeries(prev => prev.filter(s => s.id !== seriesId));
      setSuccess('Series deleted successfully');
    } catch (err) {
      setError('Failed to delete series: ' + (err.response?.data?.detail || err.message));
    }
  }, [cmsSeries, setError, setSuccess]);

  // Handle field changes in edit form
  const handleSeriesFieldChange = useCallback((field, value) => {
    setEditingSeries(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  return {
    // State
    cmsSeries,
    loadingSeries,
    editingSeries,
    showSeriesModal,
    
    // Setters (for components that need direct access)
    setEditingSeries,
    setShowSeriesModal,
    
    // Actions
    fetchCmsSeries,
    handleCreateSeries,
    handleEditSeries,
    handleSaveSeries,
    handleDeleteSeries,
    handleSeriesFieldChange,
  };
};

export default useSeriesState;
